import { pageState } from "State/page";
import type { SearchImportsData } from "Types/beRecipientsApi";
import type { PageStateType } from "Types/state";
import {
	TOAST_VARIANT_ERROR,
	TOAST_VARIANT_SUCCESS,
	toastShow,
} from "mailup-web-components";
import type { CallbackInterface } from "recoil";
import type { ImportStatus, SelectionState, TableState } from "../model";
import {
	deleteImport,
	deleteImports,
	downloadImportFile,
	getRunningImport,
	searchImports,
	startImports,
	stopImports,
} from "./services";
import {
	errorState,
	imporRunningState,
	importStatus,
	modalClauseImportState,
	modalDelesteImpostsState,
	selectionCountState,
	selectionState,
	tableImportedSearchState,
	tableImportedState,
	tableToImportState,
} from "./state";

import getIntl from "Helpers/intl";
import { logAppError } from "Helpers/logger";

export const searchToImportsCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (init: boolean, params?: SearchImportsData) => {
		if (!init) {
			set<PageStateType>(pageState, (prev) => ({ ...prev, status: "ready" }));
		} else {
			set<PageStateType>(pageState, (prev) => ({ ...prev, loading: true }));
		}
		set<TableState>(tableToImportState, (prev) => ({
			...prev,
			loading: true,
		}));
		const tableToImport = await snapshot.getPromise(tableToImportState);
		const dataIn = {
			pageSize: params?.pageSize ? params.pageSize : tableToImport.pageSize,
			pageNumber: params?.pageNumber
				? params.pageNumber
				: tableToImport.pageNumber,
			orderBy: params?.orderBy ? params.orderBy : tableToImport.orderBy,
			orderDescending: params?.orderDescending
				? params.orderDescending
				: tableToImport.orderDescending,
		};
		try {
			const resp = await searchImports(dataIn, {
				Status: [1, 2],
			});

			set<TableState>(tableToImportState, (prev) => ({
				...prev,
				loading: false,
				items: resp.Items,
				totalItems: resp.TotalItems,
				pageSize: resp.PageSize,
				pageNumber: resp.PageNumber,
				isInit: init,
			}));
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "searchToImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});
			set(errorState, true);
			set<PageStateType>(pageState, (prev) => ({
				...prev,
				status: "error",
				errorMessage: getIntl().formatMessage({
					id: "Load.Alerts.ErrorGeneric",
				}),
			}));
		}

		set<PageStateType>(pageState, (prev) => ({
			...prev,
			status: "ready",
			loading: false,
		}));
	};

export const searchImportedCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (init: boolean, params?: SearchImportsData) => {
		if (!init) {
			set<PageStateType>(pageState, (prev) => ({ ...prev, status: "ready" }));
		} else {
			set<PageStateType>(pageState, (prev) => ({ ...prev, loading: true }));
		}
		set<TableState>(tableImportedState, (prev) => ({
			...prev,
			loading: true,
		}));
		const tableImported = await snapshot.getPromise(tableImportedState);
		const tableImportedSearch = await snapshot.getPromise(
			tableImportedSearchState,
		);
		const dataIn = {
			pageSize: params?.pageSize ? params.pageSize : tableImported.pageSize,
			pageNumber: params?.pageNumber
				? params.pageNumber
				: tableImported.pageNumber,
			orderBy: params?.orderBy ? params.orderBy : tableImported.orderBy,
			orderDescending: params?.orderDescending
				? params.orderDescending
				: tableImported.orderDescending,
		};

		try {
			const resp = await searchImports(dataIn, tableImportedSearch);

			set<TableState>(tableImportedState, (prev) => ({
				...prev,
				loading: false,
				items: resp.Items,
				totalItems: resp.TotalItems,
				pageSize: resp.PageSize,
				pageNumber: resp.PageNumber,
				isInit: init,
			}));
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "searchImportedCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});
			set(errorState, true);
			set<PageStateType>(pageState, (prev) => ({
				...prev,
				status: "error",
				errorMessage: getIntl().formatMessage({
					id: "Load.Alerts.ErrorGeneric",
				}),
			}));
			set<SelectionState>(selectionState, {
				mode: "selection",
				checked: [],
				unchecked: [],
			});
		}
	};

export const deleteImportsCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async () => {
		try {
			const { id, source } = await snapshot.getPromise(
				modalDelesteImpostsState,
			);

			reset(modalDelesteImpostsState);
			if (source === "tableImported") {
				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					loading: true,
				}));
			} else {
				set<TableState>(tableToImportState, (prev) => ({
					...prev,
					loading: true,
				}));
			}
			const { mode, checked } = await snapshot.getPromise(selectionState);
			const searchState = await snapshot.getPromise(tableImportedSearchState);
			let resp = null;
			if (id > 0 || checked.length === 1) {
				resp = await deleteImport(id > 0 ? id : checked[0]);
			} else {
				const search = mode === "all" ? searchState : undefined
				resp = await deleteImports({
					IdImports: mode === "all" ? [] : checked,
					...search
				});
			}
			if (resp.Result === false) {
				throw new Error("Failed to delete imports: Result is false");
			}

			if (source === "tableImported") {
				set<SelectionState>(selectionState, {
					mode: "selection",
					checked: [],
					unchecked: [],
				});
				const tableImported = await snapshot.getPromise(tableImportedState);

				const dataIn = {
					pageSize: tableImported.pageSize,
					pageNumber: tableImported.pageNumber,
					orderBy: tableImported.orderBy,
					orderDescending: tableImported.orderDescending,
				};
				let resp = await searchImports(dataIn, searchState);
				if (resp.Items.length === 0 && resp.TotalItems > 0) {
					resp = await searchImports(
						{ ...dataIn, pageSize: tableImported.pageSize - 1 },
						searchState,
					);
				}
				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					items: resp.Items,
					totalItems: resp.TotalItems,
					pageSize: resp.PageSize,
					pageNumber: resp.PageNumber,
					isInit: false,
				}));
			} else {
				const tableToImport = await snapshot.getPromise(tableToImportState);
				const dataIn = {
					pageSize: tableToImport.pageSize,
					pageNumber: 1,
					orderBy: tableToImport.orderBy,
					orderDescending: tableToImport.orderDescending,
				};

				let resp = await searchImports(dataIn, {
					Status: [1, 2],
				});
				if (resp.Items.length === 0 && resp.TotalItems > 0) {
					resp = await searchImports(
						{ ...dataIn, pageSize: tableToImport.pageSize - 1 },
						{
							Status: [1, 2],
						},
					);
				}
				set<TableState>(tableToImportState, (prev) => ({
					...prev,
					items: resp.Items,
					totalItems: resp.TotalItems,
					pageSize: resp.PageSize,
					pageNumber: resp.PageNumber,
					isInit: false,
				}));
			}

			toastShow(
				getIntl().formatMessage({ id: "Load.FileImportStatus.DeleteSuccess" }),
				TOAST_VARIANT_SUCCESS,
			);
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "deleteImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});
			toastShow(
				getIntl().formatMessage({ id: "Load.FileImportStatus.DeleteError" }),
				TOAST_VARIANT_ERROR,
			);
		}

		set<TableState>(tableImportedState, (prev) => ({
			...prev,
			loading: false,
		}));

		set<TableState>(tableToImportState, (prev) => ({
			...prev,
			loading: false,
		}));
	};

export const checkStartImportsCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async () => {
		try {
			const resp = await getRunningImport();

			if (resp) {
				toastShow(
					getIntl().formatMessage({
						id: "Load.FileImportStatus.ImportIsRunning",
					}),
					TOAST_VARIANT_ERROR,
				);
			} else {
				set(modalClauseImportState, { show: true });
			}
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "checkStartImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});

			toastShow(
				getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
				TOAST_VARIANT_ERROR,
			);
		}
	};

export const startImportsCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async () => {
		try {
			set<ImportStatus>(importStatus, (prev) => ({
				...prev,
				status: "running",
			}));
			const resp = await startImports();
			if (resp.Result) {
			} else {
				throw new Error("Failed to start imports: Result is false");
			}
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "startImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});

			toastShow(
				getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
				TOAST_VARIANT_ERROR,
			);
		}
	};

export const stopImportsCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async () => {
		try {
			const resp = await stopImports();

			if (resp.Result) {
				set<ImportStatus>(importStatus, (prev) => ({
					...prev,
					status: "stop",
				}));
			} else {
				throw new Error("Failed to stop imports: Result is false");
			}
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "stopImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});

			toastShow(
				getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
				TOAST_VARIANT_ERROR,
			);
		}
	};

export const pollingImportsCallback =
	({ set, snapshot, reset }: CallbackInterface) =>
	async (forceStop: boolean) => {
		try {
			const { status } = await snapshot.getPromise(importStatus);

			// Se lo status non è 'running', termina il polling immediatamente

			if (status !== "running" || forceStop) {
				//ricarico le tabelle dopo aver stoppato
				set<ImportStatus>(importStatus, (prev) => ({
					...prev,
					status: "stop",
				}));
				const tableToImport = await snapshot.getPromise(tableToImportState);
				//ricarica la tabella ToImport
				set<TableState>(tableToImportState, (prev) => ({
					...prev,
					loading: true,
					isInit: false,
				}));
				const dataIn = {
					pageSize: tableToImport.pageSize,
					pageNumber: 1,
					orderBy: tableToImport.orderBy,
					orderDescending: tableToImport.orderDescending,
				};
				const pollResp = await searchImports(dataIn, {
					Status: [1, 2],
				});
				set<TableState>(tableToImportState, (prev) => ({
					...prev,
					items: pollResp.Items,
					totalItems: pollResp.TotalItems,
					pageNumber: 1,
					loading: false,
				}));
				//ricarica la tabella Imported
				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					loading: true,
				}));
				const tableImported = await snapshot.getPromise(tableImportedState);
				const dataInImported = {
					pageSize: tableImported.pageSize,
					pageNumber: tableImported.pageNumber,
					orderBy: tableImported.orderBy,
					orderDescending: tableImported.orderDescending,
				};
				const tableImportedSearch = await snapshot.getPromise(
					tableImportedSearchState,
				);
				const completedResp = await searchImports(
					dataInImported,
					tableImportedSearch,
				);
				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					loading: false,
					items: completedResp.Items,
					totalItems: completedResp.TotalItems,
					isInit: false,
				}));
				return;
			}
			const tableToImport = await snapshot.getPromise(tableToImportState);

			const dataIn = {
				pageSize: tableToImport.pageSize,
				pageNumber: 1,
				orderBy: tableToImport.orderBy,
				orderDescending: tableToImport.orderDescending,
			};

			// Controlla import con Status [1, 2] (in corso/in attesa)
			const pollResp = await searchImports(dataIn, {
				Status: [1, 2],
			});
			set<TableState>(tableToImportState, (prev) => ({
				...prev,
				items: pollResp.Items,
				totalItems: pollResp.TotalItems,
				pageNumber: 1,
				isInit: false,
			}));

			if (pollResp.TotalItems === 0 && status === "running") {
				// Non ci sono più import in corso, controlla quelli completati
				set<ImportStatus>(importStatus, (prev) => ({
					...prev,
					status: "stop",
				}));
				//ricarica la tabella Imported
				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					loading: true,
				}));
				const tableImported = await snapshot.getPromise(tableImportedState);

				const dataInImported = {
					pageSize: tableImported.pageSize,
					pageNumber: tableImported.pageNumber,
					orderBy: tableImported.orderBy,
					orderDescending: tableImported.orderDescending,
				};

				const tableImportedSearch = await snapshot.getPromise(
					tableImportedSearchState,
				);
				const completedResp = await searchImports(
					dataInImported,
					tableImportedSearch,
				);

				set<TableState>(tableImportedState, (prev) => ({
					...prev,
					loading: false,
					items: completedResp.Items,
					totalItems: completedResp.TotalItems,
					isInit: false,
				}));
			}
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "pollingImportsCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});

			toastShow(
				getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
				TOAST_VARIANT_ERROR,
			);
		}
	};

export const downloadImportFileCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (fileName: string, id: number) => {
		try {
			const resp = await downloadImportFile(fileName);
			const link = document.createElement("a");
			link.href = `data:application/octet-stream;base64,${resp.FileContent}`;
			link.download = `${id}_${encodeURIComponent(resp.FileName)}`;
			document.body.appendChild(link);
			link.click();
			link.remove();
		} catch (error) {
			logAppError({
				Component: "ImportStatus",
				Method: "downloadImportFileCallback",
				//Data: JSON.stringify({ idRecipient }),
				appError: error,
			});
			set(errorState, true);
			toastShow(
				getIntl().formatMessage({ id: "Load.Alerts.ErrorGeneric" }),
				TOAST_VARIANT_ERROR,
			);
		}
	};

export const resetSearchCallback =
	({ set, reset }: CallbackInterface) =>
	async () => {
		const ret = true;
		reset(tableImportedSearchState);
		set<SelectionState>(selectionState, {
			mode: "selection",
			checked: [],
			unchecked: [],
		});
		return ret;
	};

export const resetPageCallback =
	({ reset }: CallbackInterface) =>
	async () => {
		reset(pageState);
		reset(imporRunningState);
		reset(tableToImportState);
		reset(errorState);
		reset(tableImportedState);
		reset(selectionState);
		reset(modalDelesteImpostsState);
		reset(importStatus);
	};

export const toggleSelectRecipientCallback =
	({ set, snapshot }: CallbackInterface) =>
	async (idRecipient: number) => {
		const { checked } = await snapshot.getPromise(selectionState);

		const isChecked: boolean = checked.includes(idRecipient);

		set<SelectionState>(selectionState, ({ checked, ...prev }) => ({
			...prev,
			checked: isChecked
				? checked.filter((i) => i !== idRecipient)
				: [...checked, idRecipient],
		}));
	};

export const bulkSelectAllCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<SelectionState>(selectionState, (prev) => ({
			...prev,
			mode: "all",
		}));
	};

export const bulkSelectVisibleCallback =
	({ set, snapshot }: CallbackInterface) =>
	async () => {
		const tableState = await snapshot.getPromise(tableImportedState);

		set<SelectionState>(selectionState, ({ checked, ...prev }) => ({
			...prev,
			mode: "selection",
			checked: Array.from(
				new Set([...checked, ...tableState.items.map((r) => r.Id)]),
			),
		}));
	};

export const bulkSelectNoneCallback =
	({ set }: CallbackInterface) =>
	async () => {
		set<SelectionState>(selectionState, {
			mode: "selection",
			checked: [],
			unchecked: [],
		});
	};
